/**
 * SelectionRenderer.ts
 * 
 * Renders text selection highlights using WASM caret_map data.
 * Provides pixel-perfect highlighting across all fonts and sizes.
 */

import type { ParagraphSnapshot, CaretPosition, Line } from '../../../../core/types/Typesetter';
import type { SelectionRange } from '../../EdgeDocsTypes';

// Debug flag - set to false for production
const DEBUG_ENABLED = false; // ✅ DISABLED: Rapid typing performance fix

export interface SelectionRendererConfig {
  /** Canvas context to draw on (typically the overlay canvas) */
  ctx: CanvasRenderingContext2D;
  
  /** Selection color (CSS format) */
  color?: string;
  
  /** Selection opacity (0-1) */
  opacity?: number;
}

export class SelectionRenderer {
  private ctx: CanvasRenderingContext2D;
  private color: string;
  private opacity: number;

  constructor(config: SelectionRendererConfig) {
    this.ctx = config.ctx;
    this.color = config.color || '#0078D4'; // MS Word blue
    this.opacity = config.opacity ?? 0.3;
  }

  /**
   * Render selection highlight for a given range
   * 
   * @param selection - The selection range (anchor/focus)
   * @param snapshot - WASM paragraph snapshot with lines and caret_map
   * @param scale - Transform scale (world px → canvas px)
   */
  render(
    selection: SelectionRange,
    snapshot: ParagraphSnapshot,
    scale: number = 1.0
  ): void {
    // Clear previous selection (caller should clear canvas if needed)
    
    // Normalize selection (anchor might be > focus if selecting backwards)
    const start = Math.min(selection.anchor, selection.focus);
    const end = Math.max(selection.anchor, selection.focus);
    
    // Diagnostic logging disabled to reduce console noise
    // DEBUG_ENABLED && console.log('🎨 [SelectionRenderer] render() called:', { anchor, focus, start, end, scale });
    
    // No selection
    if (start === end) {
      DEBUG_ENABLED && console.log('🎨 [SelectionRenderer] No selection (start === end)');
      return;
    }
    
    const lines = snapshot.lines || [];
    const caretMap = snapshot.caret_map || [];
    
    if (lines.length === 0 || caretMap.length === 0) {
      DEBUG_ENABLED && console.warn('🎨 [SelectionRenderer] No lines or caret_map data');
      return;
    }
    
    DEBUG_ENABLED && console.log('🎨 [SelectionRenderer] Rendering with:', {
      linesCount: lines.length,
      caretMapCount: caretMap.length
    });
    
    // Find all lines that intersect with the selection
    for (const line of lines) {
      const lineStart = line.start_grapheme_index;
      const lineEnd = line.end_grapheme_index;
      
      // Check if this line contains any of the selection
      // Line range is [start, end) - exclusive end
      const selectionStartsInLine = start >= lineStart && start < lineEnd;
      const selectionEndsInLine = end > lineStart && end <= lineEnd;
      const selectionSpansLine = start < lineStart && end >= lineEnd;
      
      if (!selectionStartsInLine && !selectionEndsInLine && !selectionSpansLine) {
        continue; // No overlap
      }
      
      // Calculate the grapheme range to highlight on this line
      const highlightStart = Math.max(start, lineStart);
      const highlightEnd = Math.min(end, lineEnd);
      
      // Get X positions from caret_map
      const startX = this.getCaretX(caretMap, highlightStart);
      
      // ✅ FIX: Terminal caret ambiguity
      // If highlightEnd equals the line's end_grapheme_index (terminal position),
      // use the X of the last character instead, not the terminal caret.
      // The terminal caret is at the START of the next line (left margin),
      // which would make the highlight width = 0.
      let endX: number | null;
      if (highlightEnd === lineEnd) {
        // Terminal position - use last character's X instead
        const lastCharIndex = lineEnd - 1;
        if (lastCharIndex >= lineStart) {
          endX = this.getCaretX(caretMap, lastCharIndex);
          // Diagnostic logging disabled
          // DEBUG_ENABLED && console.log('🎨 [SelectionRenderer] Terminal snap:', { lineEnd, lastCharIndex, endX });
        } else {
          // Empty line or single character
          endX = this.getCaretX(caretMap, highlightEnd);
        }
      } else {
        // Not terminal - use normal caret position
        endX = this.getCaretX(caretMap, highlightEnd);
      }
      
      if (startX === null || endX === null) {
        DEBUG_ENABLED && console.warn('[SelectionRenderer] Could not find caret positions for selection', {
          highlightStart,
          highlightEnd
        });
        continue;
      }
      
      // Calculate Y position and height
      const baselineY = (line.baseline_y_fp || 0) / 64.0;
      const ascent = (line.ascent_fp || 0) / 64.0;
      const lineTopY = baselineY - ascent;
      const lineHeight = line.height_fp / 64.0;
      
      // Apply scale to convert world space → canvas space
      const rectX = startX * scale;
      const rectY = lineTopY * scale;
      const rectWidth = (endX - startX) * scale;
      const rectHeight = lineHeight * scale;
      
      // Draw the selection rectangle
      this.ctx.save();
      this.ctx.globalAlpha = this.opacity;
      this.ctx.fillStyle = this.color;
      this.ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
      this.ctx.restore();
      
      // Diagnostic logging disabled
      // DEBUG_ENABLED && console.log('🎨 [SelectionRenderer] Drew rect:', { line, highlightRange, worldRect, canvasRect });
    }
  }

  /**
   * Get the X position (in world pixels) for a grapheme from the caret_map
   */
  private getCaretX(caretMap: CaretPosition[], graphemeIndex: number): number | null {
    // Find exact match
    const caret = caretMap.find(c => c.grapheme_index === graphemeIndex);
    if (caret) {
      return (caret.x_fp || 0) / 64.0;
    }
    
    // If not found, try to interpolate or find closest
    // This shouldn't happen if WASM is working correctly
    return null;
  }

  /**
   * Update the renderer's color
   */
  setColor(color: string): void {
    this.color = color;
  }

  /**
   * Update the renderer's opacity
   */
  setOpacity(opacity: number): void {
    this.opacity = Math.max(0, Math.min(1, opacity));
  }
}

